import { useState, useEffect } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { trpc, trpcVanilla } from "~/lib/trpc"
import { Button, Card, Input, Tabs, TabsContent, TabsList, TabsTrigger } from "@aix/ui"
import toast from "react-hot-toast"

type TabValue = "voice" | "text" | "quick-taps" | "debug"

interface Coach {
  id: string
  name: string
  icon: string
  tone: "tough-love" | "chill" | "science-y"
  type: "pillar" | "side-quest"
  status: "active" | "paused" | "completed" | "archived"
}

interface Trackable {
  id: string
  question: string
  fieldKey: string
  fieldType: "boolean" | "number" | "text"
  unit?: string
  minValue?: number
  maxValue?: number
  displayOrder: number
  isRequired: boolean
}

interface ExtractionResult {
  extracted_data: Record<string, any>
  confidence: "high" | "medium" | "low"
  notes: string
}

export default function CheckInScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; date?: string }

  // Initialize tab from query param
  const initialTab = (search.tab as TabValue) || "text"
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab)

  // Check-in state
  const [checkInDate, setCheckInDate] = useState<string>(search.date || new Date().toISOString().split("T")[0])
  const [textInput, setTextInput] = useState("")
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([])

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [uploadedCheckInId, setUploadedCheckInId] = useState<string | null>(null)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)

  // Extraction review state
  const [extractedData, setExtractedData] = useState<Record<string, ExtractionResult> | null>(null)
  const [editedExtractions, setEditedExtractions] = useState<Record<string, ExtractionResult> | null>(null)
  const [coachResponses, setCoachResponses] = useState<Record<string, string> | null>(null)

  // Quick taps state
  const [quickTapsValues, setQuickTapsValues] = useState<Record<string, Record<string, any>>>({})

  // Fetch active coaches
  const { data: coachesData, isLoading: coachesLoading } = trpc.coaches.list.useQuery({
    status: "active",
  })

  const coaches: Coach[] = (coachesData?.coaches as any) || []

  // Fetch trackables for selected coaches (for quick-taps mode)
  const { data: trackablesData } = trpc.trackables.listCoachTrackables.useQuery(
    {
      coachId: selectedCoaches[0] || "",
    },
    {
      enabled: selectedCoaches.length > 0 && activeTab === "quick-taps",
    },
  )

  const trackables: Trackable[] = (trackablesData?.trackables || []) as any[]

  // tRPC mutations
  const submitCheckInMutation = trpc.brainDump.submitWithExtraction.useMutation()
  const confirmCheckInMutation = trpc.brainDump.confirmExtractions.useMutation()
  const getUploadUrlMutation = trpc.brainDump.getUploadUrl.useMutation()
  const confirmUploadMutation = trpc.brainDump.confirmUpload.useMutation()

  // Track loading states
  const isUploadingAudio = getUploadUrlMutation.isLoading || confirmUploadMutation.isLoading

  // Sync tab with query param
  useEffect(() => {
    if (search.tab && search.tab !== activeTab) {
      setActiveTab(search.tab as TabValue)
    }
  }, [search.tab])

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue)
    navigate({
      to: "/coach-os/check-in",
      // @ts-ignore
      search: (prev) => ({ ...prev, tab: value }),
    })
  }

  const handleCoachToggle = (coachId: string) => {
    if (selectedCoaches.includes(coachId)) {
      setSelectedCoaches(selectedCoaches.filter((id) => id !== coachId))
    } else {
      setSelectedCoaches([...selectedCoaches, coachId])
    }
  }

  // ============================================================================
  // VOICE MODE HANDLERS
  // ============================================================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks: BlobPart[] = []

      recorder.ondataavailable = (e) => chunks.push(e.data)
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" })
        setAudioBlob(blob)
        stream.getTracks().forEach((track) => track.stop())
      }

      recorder.start()
      setMediaRecorder(recorder)
      setIsRecording(true)
      toast.success("Recording started")
    } catch (error: any) {
      toast.error(`Failed to start recording: ${error.message}`)
    }
  }

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop()
      setIsRecording(false)
      toast.success("Recording stopped")
    }
  }

  const uploadVoiceRecording = async () => {
    if (!audioBlob) {
      toast.error("No recording to upload")
      return
    }

    try {
      toast("Uploading audio...")

      // Get upload URL
      const { uploadUrl, checkInId, r2Key } = await getUploadUrlMutation.mutateAsync({
        mediaType: "audio",
        mimeType: audioBlob.type,
        fileSize: audioBlob.size,
        checkInDate,
      })

      // Upload to R2
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": audioBlob.type,
        },
        body: audioBlob,
      })

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`)
      }

      // Confirm upload
      await confirmUploadMutation.mutateAsync({
        checkInId,
        transcriptionProvider: "deepgram",
      })

      setUploadedCheckInId(checkInId)
      toast.success("Audio uploaded! Transcription in progress...")

      // Poll for transcription completion
      pollTranscription(checkInId)
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`)
    }
  }

  const pollTranscription = (checkInId: string) => {
    const pollInterval = setInterval(() => {
      void (async () => {
        try {
          const status = await trpcVanilla.brainDump.get.query({
            checkInId,
          })

          if (status.transcriptionStatus === "completed" && status.transcription) {
            clearInterval(pollInterval)
            setTextInput(status.transcription)
            toast.success("Transcription complete!")
          } else if (status.transcriptionStatus === "failed") {
            clearInterval(pollInterval)
            toast.error("Transcription failed")
          }
        } catch (error: any) {
          clearInterval(pollInterval)
          toast.error("Failed to check transcription status")
        }
      })()
    }, 3000) // Poll every 3 seconds

    // Stop polling after 2 minutes
    setTimeout(() => clearInterval(pollInterval), 120000)
  }

  // ============================================================================
  // TEXT MODE HANDLERS
  // ============================================================================

  const submitTextCheckIn = async () => {
    if (!textInput.trim()) {
      toast.error("Please enter some text")
      return
    }

    if (selectedCoaches.length === 0) {
      toast.error("Please select at least one coach")
      return
    }

    try {
      toast("Submitting check-in and extracting data...")

      const result = await submitCheckInMutation.mutateAsync({
        checkInDate,
        inputMethod: uploadedCheckInId ? "voice" : "text",
        textInput: uploadedCheckInId ? undefined : textInput,
        checkInId: uploadedCheckInId || undefined,
        coachIds: selectedCoaches,
      })

      // Handle the response structure from submitWithExtraction
      if (result.checkIn) {
        setExtractedData((result.checkIn.coachExtractions as Record<string, ExtractionResult>) || {})
        setEditedExtractions((result.checkIn.coachExtractions as Record<string, ExtractionResult>) || {})
        setUploadedCheckInId(result.checkIn.id)
      }

      toast.success("Extraction complete! Please review the data.")
    } catch (error: any) {
      toast.error(`Failed to submit: ${error.message}`)
    }
  }

  // ============================================================================
  // QUICK-TAPS MODE HANDLERS
  // ============================================================================

  const submitQuickTaps = async () => {
    if (selectedCoaches.length === 0) {
      toast.error("Please select at least one coach")
      return
    }

    try {
      const quickTapsData: Record<string, ExtractionResult> = {}

      for (const coachId of selectedCoaches) {
        quickTapsData[coachId] = {
          extracted_data: quickTapsValues[coachId] || {},
          confidence: "high",
          notes: "Quick taps submission",
        }
      }

      const result = await submitCheckInMutation.mutateAsync({
        checkInDate,
        inputMethod: "quick-taps",
        quickTapsData,
      })

      // Get coach responses
      if (result.checkIn) {
        const checkIn = await trpcVanilla.brainDump.get.query({
          checkInId: result.checkIn.id,
        })

        const metadata = checkIn.metadata as any
        setCoachResponses(metadata?.coachResponses || {})
      }
      toast.success("Check-in submitted!")
    } catch (error: any) {
      toast.error(`Failed to submit: ${error.message}`)
    }
  }

  // ============================================================================
  // EXTRACTION CONFIRMATION
  // ============================================================================

  const confirmExtractions = async () => {
    if (!editedExtractions || !uploadedCheckInId) {
      toast.error("No extractions to confirm")
      return
    }

    try {
      toast("Confirming data and generating coach responses...")

      const result = await confirmCheckInMutation.mutateAsync({
        checkInId: uploadedCheckInId,
        confirmedExtractions: editedExtractions,
      })

      // The confirmExtractions endpoint doesn't return coachResponses yet
      // This would need to be implemented when LLM logic is added
      setCoachResponses({})
      toast.success("Check-in confirmed!")
    } catch (error: any) {
      toast.error(`Failed to confirm: ${error.message}`)
    }
  }

  const updateExtraction = (coachId: string, fieldKey: string, value: any) => {
    if (!editedExtractions) return

    setEditedExtractions({
      ...editedExtractions,
      [coachId]: {
        ...editedExtractions[coachId],
        extracted_data: {
          ...editedExtractions[coachId].extracted_data,
          [fieldKey]: value,
        },
      },
    })
  }

  const updateQuickTapsValue = (coachId: string, fieldKey: string, value: any) => {
    setQuickTapsValues({
      ...quickTapsValues,
      [coachId]: {
        ...(quickTapsValues[coachId] || {}),
        [fieldKey]: value,
      },
    })
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b-4 border-border bg-secondary p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-primary">📝 Daily Check-in</h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-2">
          Submit your daily progress - voice, text, or quick taps
        </p>

        {/* Date Selector */}
        <div className="mt-4">
          <label className="text-xs md:text-sm font-medium text-foreground">Check-in Date:</label>
          <Input
            type="date"
            value={checkInDate}
            onChange={(e) => setCheckInDate(e.target.value)}
            className="mt-1 w-full sm:max-w-xs border border-border text-sm"
          />
        </div>
      </div>

      {/* Coach Selection */}
      <div className="border-b-2 border-border bg-background p-3 md:p-4">
        <h3 className="text-xs md:text-sm font-medium mb-2">Select Coaches:</h3>
        {coachesLoading ? (
          <p className="text-xs md:text-sm text-muted-foreground">Loading coaches...</p>
        ) : coaches.length === 0 ? (
          <p className="text-xs md:text-sm text-muted-foreground">No active coaches found. Create one first!</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {coaches.map((coach) => (
              <Button
                key={coach.id}
                variant={selectedCoaches.includes(coach.id) ? "default" : "outline"}
                size="sm"
                onClick={() => handleCoachToggle(coach.id)}
                className="border-2 border-border text-xs md:text-sm"
              >
                {coach.icon} <span className="hidden xs:inline">{coach.name}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="border-b-2 border-border bg-background px-2 md:px-6 overflow-x-auto">
            <TabsList className="bg-transparent flex-nowrap w-full justify-start">
              <TabsTrigger
                value="text"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">📝 Text</span>
                <span className="sm:hidden">📝</span>
              </TabsTrigger>
              <TabsTrigger
                value="voice"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">🎤 Voice</span>
                <span className="sm:hidden">🎤</span>
              </TabsTrigger>
              <TabsTrigger
                value="quick-taps"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">⚡ Quick Taps</span>
                <span className="sm:hidden">⚡</span>
              </TabsTrigger>
              <TabsTrigger
                value="debug"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">🐛 Debug</span>
                <span className="sm:hidden">🐛</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto p-3 md:p-6">
            {/* TEXT MODE */}
            <TabsContent value="text" className="h-full m-0">
              <div className="max-w-4xl mx-auto">
                <Card className="p-4 md:p-6 border-2 border-border bg-secondary mb-4 md:mb-6">
                  <h2 className="text-base md:text-lg font-bold mb-3 md:mb-4">Text Input</h2>
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Tell me about your day... What did you accomplish? How did you feel? Any challenges?"
                    className="w-full h-32 md:h-48 border-2 border-border rounded-md px-3 py-2 bg-background resize-none text-sm md:text-base"
                  />
                  <Button
                    onClick={submitTextCheckIn}
                    loading={submitCheckInMutation.isLoading}
                    disabled={!textInput.trim() || selectedCoaches.length === 0}
                    className="mt-3 md:mt-4 w-full sm:w-auto text-xs md:text-sm"
                  >
                    Submit & Extract Data
                  </Button>
                </Card>

                {/* Extraction Review */}
                {extractedData && editedExtractions && (
                  <ExtractionReview
                    coaches={coaches}
                    extractedData={editedExtractions}
                    trackablesData={trackablesData}
                    onUpdate={updateExtraction}
                    onConfirm={confirmExtractions}
                    isConfirming={confirmCheckInMutation.isLoading}
                  />
                )}

                {/* Coach Responses */}
                {coachResponses && <CoachResponses coaches={coaches} responses={coachResponses} />}
              </div>
            </TabsContent>

            {/* VOICE MODE */}
            <TabsContent value="voice" className="h-full m-0">
              <div className="max-w-4xl mx-auto">
                <Card className="p-4 md:p-6 border-2 border-border bg-secondary mb-4 md:mb-6">
                  <h2 className="text-base md:text-lg font-bold mb-3 md:mb-4">Voice Recording</h2>

                  <div className="flex flex-col items-center gap-3 md:gap-4">
                    {!isRecording && !audioBlob && (
                      <Button onClick={startRecording} size="lg" className="w-full sm:w-48 text-sm md:text-base">
                        🎤 Start Recording
                      </Button>
                    )}

                    {isRecording && (
                      <div className="flex flex-col items-center gap-3 md:gap-4">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-destructive rounded-full animate-pulse flex items-center justify-center">
                          <span className="text-3xl md:text-4xl">🎙️</span>
                        </div>
                        <Button
                          onClick={stopRecording}
                          variant="destructive"
                          size="lg"
                          className="w-full sm:w-auto text-sm md:text-base"
                        >
                          ⏹️ Stop Recording
                        </Button>
                      </div>
                    )}

                    {audioBlob && !uploadedCheckInId && (
                      <div className="flex flex-col items-center gap-3 md:gap-4 w-full">
                        <p className="text-xs md:text-sm text-muted-foreground">
                          Recording ready ({(audioBlob.size / 1024).toFixed(1)} KB)
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto">
                          <Button
                            onClick={() => setAudioBlob(null)}
                            variant="outline"
                            className="flex-1 sm:flex-initial text-xs md:text-sm"
                          >
                            🗑️ Discard
                          </Button>
                          <Button
                            onClick={uploadVoiceRecording}
                            loading={isUploadingAudio}
                            className="flex-1 sm:flex-initial text-xs md:text-sm"
                          >
                            ⬆️ Upload & Transcribe
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {textInput && (
                    <div className="mt-4 md:mt-6">
                      <h3 className="text-xs md:text-sm font-medium mb-2">Transcription:</h3>
                      <div className="border-2 border-border rounded-md p-3 md:p-4 bg-background">
                        <p className="text-xs md:text-sm whitespace-pre-wrap">{textInput}</p>
                      </div>
                      <Button
                        onClick={submitTextCheckIn}
                        loading={submitCheckInMutation.isLoading}
                        disabled={selectedCoaches.length === 0}
                        className="mt-3 md:mt-4 w-full sm:w-auto text-xs md:text-sm"
                      >
                        Extract Data from Transcription
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Extraction Review (same as text mode) */}
                {extractedData && editedExtractions && (
                  <ExtractionReview
                    coaches={coaches}
                    extractedData={editedExtractions}
                    trackablesData={trackablesData}
                    onUpdate={updateExtraction}
                    onConfirm={confirmExtractions}
                    isConfirming={confirmCheckInMutation.isLoading}
                  />
                )}

                {/* Coach Responses */}
                {coachResponses && <CoachResponses coaches={coaches} responses={coachResponses} />}
              </div>
            </TabsContent>

            {/* QUICK-TAPS MODE */}
            <TabsContent value="quick-taps" className="h-full m-0">
              <div className="max-w-4xl mx-auto">
                {selectedCoaches.length === 0 ? (
                  <Card className="p-6 border-2 border-border bg-secondary">
                    <p className="text-muted-foreground text-center">
                      Please select at least one coach above to get started.
                    </p>
                  </Card>
                ) : (
                  <>
                    <QuickTapsForm
                      coaches={coaches.filter((c) => selectedCoaches.includes(c.id))}
                      trackables={trackables}
                      values={quickTapsValues}
                      onUpdate={updateQuickTapsValue}
                    />
                    <div className="mt-6">
                      <Button
                        onClick={submitQuickTaps}
                        loading={submitCheckInMutation.isLoading}
                        size="lg"
                        className="w-full"
                      >
                        Submit Quick Taps
                      </Button>
                    </div>
                  </>
                )}

                {/* Coach Responses */}
                {coachResponses && <CoachResponses coaches={coaches} responses={coachResponses} />}
              </div>
            </TabsContent>

            {/* DEBUG TAB */}
            <TabsContent value="debug" className="h-full m-0">
              <div className="max-w-4xl mx-auto">
                <Card className="p-6 border-2 border-border bg-secondary">
                  <h2 className="text-lg font-bold mb-4">Debug Data</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Selected Coaches:</h3>
                      <pre className="text-xs bg-background p-4 rounded border border-border overflow-auto">
                        {JSON.stringify(selectedCoaches, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-2">Extracted Data:</h3>
                      <pre className="text-xs bg-background p-4 rounded border border-border overflow-auto max-h-96">
                        {JSON.stringify(extractedData, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-2">Coach Responses:</h3>
                      <pre className="text-xs bg-background p-4 rounded border border-border overflow-auto">
                        {JSON.stringify(coachResponses, null, 2)}
                      </pre>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}

// ============================================================================
// EXTRACTION REVIEW COMPONENT
// ============================================================================

function ExtractionReview({
  coaches,
  extractedData,
  trackablesData,
  onUpdate,
  onConfirm,
  isConfirming,
}: {
  coaches: Coach[]
  extractedData: Record<string, ExtractionResult>
  trackablesData?: { trackables: any[] }
  onUpdate: (coachId: string, fieldKey: string, value: any) => void
  onConfirm: () => void
  isConfirming: boolean
}) {
  return (
    <Card className="p-6 border-2 border-border bg-secondary mb-6">
      <h2 className="text-lg font-bold mb-4">Review Extracted Data</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Please verify the extracted data below. You can edit any values before confirming.
      </p>

      <div className="space-y-6">
        {Object.entries(extractedData).map(([coachId, extraction]) => {
          const coach = coaches.find((c) => c.id === coachId)
          if (!coach) return null

          return (
            <Card key={coachId} className="p-4 border border-border bg-background">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">{coach.icon}</span>
                <div>
                  <h3 className="font-bold">{coach.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    Confidence: {extraction.confidence} • {extraction.notes}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(extraction.extracted_data).map(([fieldKey, value]) => (
                  <div key={fieldKey} className="flex items-center gap-4">
                    <label className="text-sm font-medium min-w-[200px]">{fieldKey}:</label>
                    {typeof value === "boolean" ? (
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => onUpdate(coachId, fieldKey, e.target.checked)}
                        className="w-5 h-5 border-2 border-border rounded"
                      />
                    ) : typeof value === "number" ? (
                      <Input
                        type="number"
                        value={value}
                        onChange={(e) => onUpdate(coachId, fieldKey, parseFloat(e.target.value))}
                        className="max-w-[150px] border border-border"
                      />
                    ) : (
                      <Input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onUpdate(coachId, fieldKey, e.target.value)}
                        className="flex-1 border border-border"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )
        })}
      </div>

      <Button onClick={onConfirm} loading={isConfirming} size="lg" className="mt-6 w-full">
        ✅ Confirm & Generate Coach Responses
      </Button>
    </Card>
  )
}

// ============================================================================
// COACH RESPONSES COMPONENT
// ============================================================================

function CoachResponses({ coaches, responses }: { coaches: Coach[]; responses: Record<string, string> }) {
  return (
    <Card className="p-6 border-2 border-border bg-secondary">
      <h2 className="text-lg font-bold mb-4">🎉 Coach Responses</h2>

      <div className="space-y-4">
        {Object.entries(responses).map(([coachId, response]) => {
          const coach = coaches.find((c) => c.id === coachId)
          if (!coach) return null

          return (
            <Card key={coachId} className="p-4 border border-border bg-background">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{coach.icon}</span>
                <h3 className="font-bold">{coach.name}</h3>
                <span className="text-xs text-muted-foreground ml-auto">{coach.tone}</span>
              </div>
              <p className="text-sm">{response}</p>
            </Card>
          )
        })}
      </div>
    </Card>
  )
}

// ============================================================================
// QUICK-TAPS FORM COMPONENT
// ============================================================================

function QuickTapsForm({
  coaches,
  trackables,
  values,
  onUpdate,
}: {
  coaches: Coach[]
  trackables: Trackable[]
  values: Record<string, Record<string, any>>
  onUpdate: (coachId: string, fieldKey: string, value: any) => void
}) {
  return (
    <div className="space-y-6">
      {coaches.map((coach) => (
        <Card key={coach.id} className="p-6 border-2 border-border bg-secondary">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">{coach.icon}</span>
            <h2 className="text-lg font-bold">{coach.name}</h2>
          </div>

          <div className="space-y-4">
            {trackables.length === 0 ? (
              <p className="text-sm text-muted-foreground">No trackables configured for this coach.</p>
            ) : (
              trackables.map((trackable) => (
                <div key={trackable.id} className="flex items-center gap-4">
                  <label className="text-sm font-medium flex-1">
                    {trackable.question}
                    {trackable.isRequired && <span className="text-destructive ml-1">*</span>}
                  </label>

                  {trackable.fieldType === "boolean" ? (
                    <input
                      type="checkbox"
                      checked={values[coach.id]?.[trackable.fieldKey] || false}
                      onChange={(e) => onUpdate(coach.id, trackable.fieldKey, e.target.checked)}
                      className="w-5 h-5 border-2 border-border rounded"
                    />
                  ) : trackable.fieldType === "number" ? (
                    <Input
                      type="number"
                      value={values[coach.id]?.[trackable.fieldKey] || ""}
                      onChange={(e) => onUpdate(coach.id, trackable.fieldKey, parseFloat(e.target.value))}
                      placeholder={trackable.unit || "Enter value"}
                      className="max-w-[150px] border border-border"
                      min={trackable.minValue}
                      max={trackable.maxValue}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={values[coach.id]?.[trackable.fieldKey] || ""}
                      onChange={(e) => onUpdate(coach.id, trackable.fieldKey, e.target.value)}
                      placeholder="Enter text"
                      className="max-w-xs border border-border"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}
